import useHover from 'hooks/useHover'
import { Check, X } from 'lucide-react'
import { FC, useState } from 'react'
import {
  cn,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@wellesley/ui'

import getDefaultPrivacy from '../../helpers/privacy/getDefaultPrivacy'

import PrivacyItem from './PrivacyItem'
import DisabledCommentsMenu from './EnableCommentsMenu'
import { TPrivacyDropdown } from './types'

const PrivacyDropdown: FC<TPrivacyDropdown> = ({
  privacy,
  onPrivacyChange,
  isReply,
  isDisabledCommentOption = true,
  privacyOptions,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [ref, isHovered] = useHover<HTMLDivElement>()

  const defaultPrivacyValue = getDefaultPrivacy(isReply)
  const isDefaultPrivacy = privacy === defaultPrivacyValue
  const isActiveTriggerIcon = isOpen || isHovered || !isDefaultPrivacy

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          'duration-custom gap-xxxs relative flex h-10 w-6 items-center justify-between transition-all',
          {
            'p-xxs w-16 rounded-[6px] bg-gray-100': !isDefaultPrivacy,
          },
        )}
      >
        <DropdownMenuTrigger
          data-testid="privacy-button"
          className={cn(
            'cursor-pointer rounded-[3px] text-base focus:outline-none',
          )}
          onClick={() => {
            setIsOpen(true)
          }}
        >
          <div
            ref={ref}
            className={cn(
              'cursor-pointer [&_svg]:pointer-events-none',
              !isActiveTriggerIcon && '[&_svg]:stroke-[var(--gray-500)]',
            )}
          >
            {(
              privacyOptions.find((p) => p.key === privacy) || privacyOptions[0]
            )?.icon(24, isActiveTriggerIcon)}
          </div>
        </DropdownMenuTrigger>
        {!isDefaultPrivacy && (
          <button
            data-testid="privacy-reset"
            onClick={() => {
              onPrivacyChange(defaultPrivacyValue)
            }}
          >
            <X size={16} color="var(--gray-400)" className="cursor-pointer" />
          </button>
        )}
      </div>
      <DropdownMenuContent
        align="start"
        data-testid="privacy-menu"
        className="gap-xxs !pt-xs flex max-w-[240px] flex-col"
      >
        {privacyOptions.map((props) => (
          <DropdownMenuItem
            key={props.key}
            className={cn(
              `gap-xxs px-xxs py-xxxs flex min-h-11 cursor-pointer flex-row items-center hover:bg-gray-50`,
              { 'bg-gray-100': props.key === privacy },
            )}
            onClick={() => onPrivacyChange(props.key)}
          >
            <PrivacyItem className="flex-1" {...props} />
            {props.key === privacy && (
              <Check size={20} className="text-gray-900" />
            )}
          </DropdownMenuItem>
        ))}
        {isDisabledCommentOption && <DisabledCommentsMenu {...otherProps} />}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { PrivacyDropdown }
